import { useCallback } from 'react';

import type { UserGroupColor, UserGroupIcon } from '@frontend/api';

import { useAppSelector } from 'store/hooks';
import { getIsSuperuser } from 'store/session/selectors';

import { useUserGroupCollectionContext } from '../UserGroupCollectionProvider';

type UserGroupBadgeProps = {
  name: string;
  colorPalette: UserGroupColor;
  icon: UserGroupIcon;
};

type UserGroupBadgeLookup = (ownerId: number) => UserGroupBadgeProps | null;

/**
 * Provides a lookup function that maps an owner_id to UserGroupBadge props.
 * Uses full collection data so badges can resolve any site in the collection
 * (needed when the viewer is a superuser).
 * For non–superusers, returns null unless they belong to more than one site in
 * the collection — matching nameplate / multisite dashboard eligibility.
 */
export const useUserGroupBadgeLookup = (): UserGroupBadgeLookup => {
  const { getUserGroupById, availableGroups } = useUserGroupCollectionContext();
  const isSuperuser = useAppSelector(getIsSuperuser);

  return useCallback(
    (ownerId: number) => {
      if (!isSuperuser && availableGroups.length <= 1) {
        return null;
      }
      const group = getUserGroupById(ownerId);
      return group
        ? {
            name: group.businessName,
            colorPalette: group.color,
            icon: group.icon,
          }
        : null;
    },
    [availableGroups.length, getUserGroupById, isSuperuser],
  );
};
